/**
 * Page scroll lock shared by the bundle builder's modals (Notify Me) and the personalisation
 * visualizer.
 *
 * Both used to lock the page by putting `overflow: hidden` on the body and nothing else. On any
 * platform that draws a space-taking scrollbar — Windows, and macOS set to "always show
 * scrollbars" — hiding overflow removes the scrollbar, so the page instantly gets ~15px wider
 * while the modal is open and snaps back when it closes. Shoppers read that snap-back as the
 * carousel "shrinking" after a modal. Reserving the scrollbar's width as body padding for as
 * long as the lock is held keeps the layout perfectly still.
 *
 * The width has to be measured BEFORE overflow changes, while the scrollbar is still laid out —
 * that is the whole reason this lives in one helper instead of at each call site.
 *
 * Locks are reference counted. The visualizer and its portal loader can both hold one at the
 * same time, and the page must stay locked until the last of them lets go.
 */
window.ScrollLock =
  window.ScrollLock ||
  (function () {
    let depth = 0;
    let previousPaddingRight = '';
    const heldClasses = new Set();

    const applyLock = () => {
      const body = document.body;
      // Measured first: adding the class below is what removes the scrollbar.
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      previousPaddingRight = body.style.paddingRight;
      heldClasses.forEach((className) => body.classList.add(className));

      if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    };

    const releaseLock = () => {
      const body = document.body;

      heldClasses.forEach((className) => body.classList.remove(className));
      heldClasses.clear();
      body.style.paddingRight = previousPaddingRight;
      previousPaddingRight = '';
    };

    return {
      /**
       * @param {string} className - body class this caller locks with, e.g. 'overflow-hidden'.
       */
      lock(className) {
        if (className) heldClasses.add(className);
        depth += 1;

        if (depth === 1) {
          applyLock();
        } else if (className) {
          // Already locked, so the padding is in place — just honour the extra class.
          document.body.classList.add(className);
        }
      },

      unlock() {
        if (depth === 0) return;

        depth -= 1;
        if (depth === 0) releaseLock();
      },
    };
  })();
